import { useEffect, useState } from "react";
import axiosAdmin from "./axiosAdmin";
import { 
  Eye, Search, Calendar, Store, 
  FileText, XCircle, ChevronLeft, ChevronRight 
} from "lucide-react";

export default function AdminSubscriptionPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [proofModal, setProofModal] = useState(null);

  // ÉTATS POUR LA PAGINATION FRONTEND
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const res = await axiosAdmin.get("/subscriptions/admin"); 
      setPayments(res.data.payments || []);
    } catch (err) {
      console.error("Erreur chargement paiements", err);
    } finally {
      setLoading(false);
    }
  };

  const getProofUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `https://souqi-api.onrender.com${cleanPath}`;
  };

  // 1. FILTRAGE
  const filteredPayments = payments.filter((p) => {
    const merchantName = p.merchantId?.name || "";
    return merchantName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // 2. CALCULS DE PAGINATION
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="min-h-screen p-8 bg-[#F8FAFC] font-sans">
      
      {/* HEADER */}
      <div className="flex flex-col gap-6 mb-8 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-black text-slate-900">
            <FileText className="text-indigo-600" size={28} />
            Registre des Paiements
          </h1>
          <p className="italic font-medium text-slate-500">Consultation des justificatifs mensuels</p>
        </div>
        
        <div className="px-6 py-3 text-center bg-white border shadow-sm rounded-2xl border-slate-200">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Total Documents</span>
            <span className="text-xl font-black text-slate-800">{filteredPayments.length}</span>
        </div>
      </div>

      {/* RECHERCHE */}
      <div className="p-2 mb-6 bg-white border shadow-sm rounded-2xl border-slate-200">
        <div className="relative">
          <Search size={20} className="absolute -translate-y-1/2 left-4 top-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un marchand (ex: EU Stock...)"
            className="w-full py-3 pl-12 pr-4 font-bold border-none rounded-xl focus:ring-0 text-slate-700 placeholder:text-slate-300"
            value={searchTerm}
            onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset à la page 1 lors d'une recherche
            }}
          />
        </div>
      </div>

      {/* TABLEAU */}
      <div className="overflow-hidden bg-white border shadow-xl rounded-3xl border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b bg-slate-50 border-slate-100">
                <th className="px-8 py-5 text-xs font-black tracking-wider uppercase text-slate-400">Marchand</th>
                <th className="px-8 py-5 text-xs font-black tracking-wider uppercase text-slate-400">Période Concernée</th>
                <th className="px-8 py-5 text-xs font-black tracking-wider text-right uppercase text-slate-400">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr><td colSpan="3" className="py-20 font-bold text-center text-slate-300 animate-pulse">Chargement du registre...</td></tr>
              ) : currentItems.length === 0 ? (
                <tr><td colSpan="3" className="py-20 font-bold text-center text-slate-300">Aucun résultat.</td></tr>
              ) : (
                currentItems.map((payment) => (
                  <tr key={payment._id} className="transition-colors hover:bg-slate-50/50">
                    <td className="px-8 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-500">
                          <Store size={16} />
                        </div>
                        <span className="font-extrabold text-slate-700">{payment.merchantId?.name}</span>
                      </div>
                    </td>
                    <td className="px-8 py-4">
                      <div className="inline-flex items-center gap-2 px-3 py-1 text-sm font-black text-indigo-700 rounded-lg bg-indigo-50">
                        <Calendar size={14} />
                        {payment.month.toString().padStart(2, '0')} / {payment.year}
                      </div>
                    </td>
                    <td className="px-8 py-4 text-right">
                      <button
                        onClick={() => setProofModal(getProofUrl(payment.proofImage))}
                        className="px-6 py-2 text-xs font-black text-white transition-all bg-slate-900 rounded-xl hover:bg-indigo-600 active:scale-95"
                      >
                        VOIR REÇU
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION CONTROLS */}
        {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between px-8 py-4 border-t bg-slate-50 border-slate-100">
                <p className="text-xs font-bold text-slate-400">
                    Affichage {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredPayments.length)} sur {filteredPayments.length}
                </p>
                <div className="flex items-center gap-2">
                    <button 
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 bg-white border rounded-lg border-slate-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    
                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i + 1}
                            onClick={() => paginate(i + 1)}
                            className={`w-8 h-8 rounded-lg text-xs font-black transition-all ${
                                currentPage === i + 1 
                                ? "bg-indigo-600 text-white shadow-md shadow-indigo-200" 
                                : "bg-white text-slate-400 border border-slate-200 hover:border-indigo-300"
                            }`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button 
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 bg-white border rounded-lg border-slate-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
            </div>
        )}
      </div>

      {/* MODALE */}
      {proofModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/90 backdrop-blur-md">
          <div className="relative w-full max-w-2xl bg-white shadow-2xl rounded-[2.5rem] overflow-hidden">
            <button
              onClick={() => setProofModal(null)}
              className="absolute p-2 transition-all rounded-full top-6 right-6 bg-slate-100 text-slate-500 hover:bg-red-500 hover:text-white"
            >
              <XCircle size={24} />
            </button>
            <div className="flex items-center justify-center p-8 bg-slate-50">
              <img
                src={proofModal}
                alt="Preuve"
                className="max-w-full max-h-[70vh] object-contain rounded-2xl shadow-lg"
              />
            </div>
            <div className="p-6 text-center">
                <button 
                    onClick={() => setProofModal(null)}
                    className="px-10 py-3 font-black text-white bg-slate-900 rounded-2xl"
                >
                    Fermer l'aperçu
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}